<template>
  <panel light>
    <h5 slot="heading" class="panel-title">{{pageTitle}}</h5>
    <div slot="heading" class="heading-elements">
      <div class="heading-buttons">
        <router-link :to="{name: 'product/create', query: filter}" class="btn btn-success">Ajouter un produit</router-link>
      </div>
    </div>
    <div slot="body" class="form-inline">
      <form-group>
        <form-input-search :value="filter.search" debounce @input="setFilterSearch"></form-input-search>
      </form-group>
      <form-group>
        <form-select :value="filter.kind" @input="setFilterKind" :options="ProductKinds" placeholder="Filtrer par type"></form-select>
      </form-group>
      <form-group>
        <form-select :value="filter.collection" @input="setFilterCollection" :options="collections" placeholder="Filtrer par collection"></form-select>
      </form-group>
      <form-group>
        <button v-if="isFiltered" type="button" class="btn btn-default" @click="resetFilter()">Effacer les filtres</button>
      </form-group>
    </div>

    <div slot="content" class="table-responsive">
      <table class="table table-striped table-lg">
        <thead>
        <tr>
          <th width="135">Aperçu</th>
          <th>Nom du produit</th>
          <th>Type</th>
          <th>Déclinaisons</th>
          <th>Activité</th>
          <th width="100" class="text-center">Statut</th>
          <th width="80" class="text-center">Actions</th>
        </tr>
        </thead>
        <tbody v-if="products.length">
        <tr v-for="product in products" :key="product.id">
          <td class="centered">
            <media-image :media="product.preview" empty-label="Pas d'image" thumbnail />
          </td>
          <td>
            <span class="label label-default" :style="getCollectionStyle(product)">{{getCollectionName(product)}}</span>
            <h6 class="no-margin text-semibold">
              <router-link :to="{name: 'product/edit', params: {id: product.id}}">{{getName(product)}}</router-link>
            </h6>
          </td>
          <td class="text-nowrap">
            {{getKindName(product)}}
          </td>
          <td class="text-nowrap">
            <router-link :to="{name: 'product/variants', params: {id: product.id}}">{{product.variants.length}} déclinaison(s)</router-link>
          </td>
          <td class="text-nowrap">
            <activity :value="product"></activity>
          </td>
          <td class="text-center">
            <publishing-status class="cursor-pointer" :value="product.published" @hide="setProductPublished(product, false)" @show="setProductPublished(product)"></publishing-status>
          </td>
          <td class="text-center">
            <ul is="table-row-actions">
              <li>
                <router-link :to="{name: 'product/edit', params: {id: product.id}}"><i class="icon-pencil7"></i> Modifier</router-link>
              </li>
              <li>
                <router-link :to="{name: 'product/variants', params: {id: product.id}}"><i class="icon-color-sampler"></i> Gérer les déclinaisons</router-link>
              </li>
              <li class="divider"></li>
              <li><a @click="deleteProduct(product)"><i class="icon-bin"></i> Supprimer</a></li>
            </ul>
          </td>
        </tr>
        </tbody>
        <tbody v-else-if="$apollo.loading">
        <tr>
          <td colspan="7" class="text-center">
            <h4>Chargement&hellip;</h4>
          </td>
        </tr>
        </tbody>
        <tbody v-else>
        <tr>
          <td colspan="7" class="text-center">
            <h4>Aucun résultat</h4>
            <router-link :to="{name: 'product/create', query: filter}" class="btn btn-success">Ajouter un produit</router-link>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <pagination slot="footer" :total="results.totalCount" v-model="pagination"></pagination>
  </panel>
</template>

<script>
import { mapActions } from "vuex";

import _filter from "lodash/filter";
import _map from "lodash/map";
import _some from "lodash/some";

import FormInputSearch from "package-common-vuejs/src/components/form/FormInputSearch";
import Pagination from "package-common-vuejs/src/components/Pagination";
import TableRowActions from "package-common-vuejs/src/components/table/TableRowActions";
import MediaImage from "../../components/MediaImage";
import PublishingStatus from "../../components/PublishingStatus";
import Activity from "../../components/Activity";

import ProductListQuery from "./graphql/ProductListQuery.gql";
import ProductRemoveMutation from "./graphql/ProductRemoveMutation.gql";
import CollectionListQuery from "../collections/graphql/CollectionListQuery.gql";
import ProductUpdateMutation from "./graphql/ProductUpdateMutation.gql";

import { ProductKinds, ProductKindsArray } from "../../common/types";

function hashCode(string = '') {
  let hash = 0, i, chr;
  if (string.length === 0) return hash;
  for (i = 0; i < string.length; i++) {
    chr   = string.charCodeAt(i);
    hash  = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export default {
  components: {
    MediaImage,
    FormInputSearch,
    Pagination,
    TableRowActions,
    PublishingStatus,
    Activity
  },
  data() {
    return {
      results: {
        totalCount: 0,
        edges: []
      },
      collections: [],

      ProductKinds: ProductKindsArray
    };
  },
  apollo: {
    results: {
      query: ProductListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            after: this.pagination.offset,
            limit: this.pagination.limit
          },
          filter: { ...this.filter }
        };
      }
    },
    collections: {
      query: CollectionListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999
          }
        };
      },
      update: data =>
        _map(data.results.edges, ({ node: { id, name } }) => ({
          value: id,
          label: name
        }))
    }
  },
  computed: {
    products() {
      return _map(this.results.edges, "node");
    },
    filter() {
      return this.$store.state.products.filter;
    },
    pagination: {
      get() {
        return this.$store.state.products.pagination;
      },
      set(value) {
        this.setPagination(value);
      }
    },
    isFiltered() {
      return _some(this.filter);
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$apollo.queries.results.refetch();
    });
  },
  beforeMount() {
    const { collection = null } = this.$route.query || {};
    if (collection) {
      this.setFilterCollection(collection);
    }
  },
  methods: {
    ...mapActions("products", [
      "setFilterSearch",
      "setFilterKind",
      "setFilterCollection",
      "setPagination",
      "resetFilter"
    ]),
    getName({ name, combination, combinationPart }) {
      return _filter([
        combination && combination.name,
        combination &&
          combination.parts &&
          combination.parts.length > 1 &&
          combinationPart &&
          combinationPart.name,
        name
      ]).join(" / ");
    },
    getKindName({ kind }) {
      return ProductKinds[kind];
    },
    getCollectionName({ collection }) {
      return collection && collection.name;
    },
    getCollectionStyle({ collection }) {
      const hash = hashCode((collection && collection.name) || '');
      return `background: hsl(${hash % 360}, 30%, 50%); border-color: hsl(${hash % 360}, 20%, 45%);`;
    },
    async setProductPublished(product, published = true) {
      try {
        await this.$apollo.mutate({
          mutation: ProductUpdateMutation,
          variables: {
            id: product.id,
            data: { published }
          }
        });
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    },
    async deleteProduct(product) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: ProductRemoveMutation,
          variables: {
            id: product.id
          }
        });
        if (data.result) {
          this.$store.dispatch(
            "addNotificationSuccess",
            `Le produit a été supprimé`
          );
          this.$apollo.queries.results.refetch();
        } else {
          this.$store.dispatch(
            "addNotificationError",
            `Le produit n'a pas pu être supprimé`
          );
        }
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/products/ProductsList.vue